/**
 * 売掛・買掛・未払残高(B4・2026-10-23・Board)。月末時点の残高を事業部別に出し、下に明細を出す。
 * 集計は balances.ts、読み込みは balancesRepo.ts。書き込みはしない(表示のみ)。
 *
 *  - 見る人: 「確定」の権限の人(経理・管理者・承認者。入出金の記録を読めるのはこの人たち。2026-10-23 発注者決定⑥で承認者も)
 *  - Board のメニューに出す(2026-10-23 発注者決定⑦。旧: 2026-10-22 決定5 はメニューに出さない)
 *  - 月はトップバーの月(その月末時点)
 *  - 仮払金は売掛・買掛に足さず、別の列で出す(2026-10-22 決定1)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMasters } from '@core/masters/MastersProvider';
import { usePeriod } from '@core/components/PeriodProvider';
import { useToast } from '@core/components/Toast';
import { endOfMonth, toMonthLabel, toShortDate } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { fetchUsers } from '@core/masters/masterRepo';
import { fetchVendors } from '@core/masters/vendorRepo';
import { PAYMENT_METHOD_LABELS, STATUS_LABELS, type DepartmentId, type Transaction } from '@core/lib/types';
import { BALANCE_RULES, computeBalances, type Balances } from './balances';
import { fetchBalanceSources } from './balancesRepo';

/** 表の下の説明(決まり BALANCE_RULES に合わせて文を変える) */
function noteText(): string {
  const from = BALANCE_RULES.receivableFrom;
  return [
    '残高 = 月末までに PL に入った額 − 月末までに実際にお金が動いた額。行を押すとその事業部の明細に絞ります。',
    `売掛は${from ? `切り替え日(${toShortDate(from)})以降に計上した売上のみ` : '計上したすべての売上'}${
      BALANCE_RULES.includeUnbilled ? '(請求書を作る前の売上を含む)' : '(請求書を作った売上のみ)'
    }。`,
    '買掛・未払は本人払いの経費・立替を含みます(本人が支払い・精算されるまで。クレカは引き落とし日まで)。',
    '売上・原価入力で入れた原価は、入力した時点で支払い済みとして扱います。',
    `仮払金は売掛・買掛に足していません${BALANCE_RULES.prepaid === 'both' ? '(仮払いの経費は本人が支払うまで買掛・未払にも入ります)' : '(経理が仮払いする前は未払に入ります)'}。`,
  ].join('');
}

type Tab = 'receivable' | 'payable' | 'prepaid';
const TABS: { value: Tab; label: string }[] = [
  { value: 'receivable', label: '売掛' },
  { value: 'payable', label: '買掛・未払' },
  { value: 'prepaid', label: '仮払金' },
];

type Sources = Awaited<ReturnType<typeof fetchBalanceSources>> & { month: string };

export default function BalancesPage() {
  const { departments, departmentName, loading: mastersLoading } = useMasters();
  const { month } = usePeriod();
  const toast = useToast();
  const [sources, setSources] = useState<Sources | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('payable');
  const [deptFilter, setDeptFilter] = useState<DepartmentId | ''>('');
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const [vendorNames, setVendorNames] = useState<Map<string, string>>(new Map());
  const asOf = endOfMonth(month);

  // 読み込みの追い越し対策: 月を素早く切り替えたとき、遅れて返った古い結果で上書きしない
  const requestNo = useRef(0);
  const load = useCallback(async () => {
    const no = ++requestNo.current;
    setLoading(true);
    try {
      const s = await fetchBalanceSources(month);
      if (no !== requestNo.current) return;
      setSources({ ...s, month });
    } catch (e) {
      if (no !== requestNo.current) return;
      toast(e instanceof Error ? e.message : '残高を読み込めませんでした');
      setSources(null);
    } finally {
      if (no === requestNo.current) setLoading(false);
    }
  }, [month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // 明細で申請者・支払先の名前を出すために読む(読めなくても残高は出す)
  useEffect(() => {
    let alive = true;
    fetchUsers()
      .then((users) => alive && setUserNames(new Map(users.map((u) => [u.id, u.display_name]))))
      .catch(() => undefined);
    fetchVendors(true)
      .then((vs) => alive && setVendorNames(new Map(vs.map((v) => [v.id, v.name]))))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  // 読み込んだ月と今の月が違う間(月を切り替えた直後)は計算しない(古いデータで一瞬違う数字を出さない)
  const balances: Balances | null = useMemo(
    () =>
      sources && sources.month === month
        ? computeBalances({ asOf, transactions: sources.transactions, invoices: sources.invoices, cashEntries: sources.cashEntries })
        : null,
    [sources, month, asOf],
  );

  // 事業部はマスタの順に。残高のある事業部と、有効な事業部を出す
  const deptRows = useMemo(() => {
    if (!balances) return [];
    const map = new Map(balances.byDepartment.map((d) => [d.departmentId, d]));
    const ids = [
      ...departments.filter((d) => d.active !== false || map.has(d.id)).map((d) => d.id),
      ...balances.byDepartment.map((d) => d.departmentId).filter((id) => !departments.some((d) => d.id === id)),
    ];
    return ids.map((id) => map.get(id) ?? { departmentId: id, receivable: 0, payable: 0, prepaid: 0 });
  }, [balances, departments]);

  const partyOf = (t: Transaction) => (t.vendor_id ? vendorNames.get(t.vendor_id) : null) ?? t.counterparty ?? '—';
  const inDept = (t: Transaction) => !deptFilter || t.department_id === deptFilter;

  if (loading || mastersLoading || !balances) {
    return (
      <section className="screen">
        <div className="card">
          <p className="empty">{loading || mastersLoading ? '読み込み中…' : '表示できるデータがありません'}</p>
        </div>
      </section>
    );
  }

  const filterName = deptFilter ? departmentName(deptFilter) : '全社';

  return (
    <section className="screen">
      <div className="card">
        <h3>売掛・買掛・未払残高({toMonthLabel(month)}末時点)</h3>
        <table>
          <thead>
            <tr>
              <th>事業部</th>
              <th className="num">売掛残高</th>
              <th className="num">買掛・未払残高</th>
              <th className="num">仮払金(別)</th>
            </tr>
          </thead>
          <tbody>
            {deptRows.map((d) => (
              <tr
                key={d.departmentId}
                className="pl-row"
                style={{ cursor: 'pointer', background: deptFilter === d.departmentId ? 'var(--blue-bg)' : undefined }}
                onClick={() => setDeptFilter(deptFilter === d.departmentId ? '' : d.departmentId)}
              >
                <td>{departmentName(d.departmentId)}</td>
                <td className={`num${d.receivable < 0 ? ' neg' : ''}`}>{formatYen(d.receivable)}</td>
                <td className="num">{formatYen(d.payable)}</td>
                <td className="num">{formatYen(d.prepaid)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 700 }}>全社合計</td>
              <td className={`num${balances.total.receivable < 0 ? ' neg' : ''}`} style={{ fontWeight: 700 }}>
                {formatYen(balances.total.receivable)}
              </td>
              <td className="num" style={{ fontWeight: 700 }}>
                {formatYen(balances.total.payable)}
              </td>
              <td className="num" style={{ fontWeight: 700 }}>
                {formatYen(balances.total.prepaid)}
              </td>
            </tr>
          </tbody>
        </table>
        <p className="hint" style={{ marginTop: 10 }}>{noteText()}</p>
      </div>

      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          明細({filterName})
          {deptFilter && (
            <button className="chip" onClick={() => setDeptFilter('')}>
              全社に戻す
            </button>
          )}
        </h3>
        <div className="chips" style={{ marginBottom: 12 }}>
          {TABS.map((o) => (
            <button key={o.value} className={`chip${tab === o.value ? ' on' : ''}`} onClick={() => setTab(o.value)}>
              {o.label}
            </button>
          ))}
        </div>

        {tab === 'receivable' && (
          <ReceivableTable rows={balances.receivables.filter((r) => inDept(r.transaction))} partyOf={partyOf} deptName={departmentName} />
        )}
        {tab === 'payable' && (
          <PayableTable rows={balances.payables.filter((r) => inDept(r.transaction))} partyOf={partyOf} deptName={departmentName} />
        )}
        {tab === 'prepaid' && (
          <PrepaidTable
            rows={balances.prepaids.filter((r) => inDept(r.transaction))}
            userName={(id) => (id ? (userNames.get(id) ?? '—') : '—')}
            deptName={departmentName}
          />
        )}
      </div>
    </section>
  );
}

type NameOf = (t: Transaction) => string;

/** 支払い済みでも未払に残る理由を状態の欄に出す(立替は精算待ち・クレカは引き落とし待ち) */
function payableState(t: Transaction): string {
  if (t.status === 'paid' || t.status === 'completed') {
    if (t.advance) return '精算待ち';
    if (t.payment_method === 'card') return '引き落とし待ち';
  }
  return STATUS_LABELS[t.status];
}

function Empty() {
  return <p className="empty">残高のある取引はありません</p>;
}

function ReceivableTable({ rows, partyOf, deptName }: { rows: Balances['receivables']; partyOf: NameOf; deptName: (id: string) => string }) {
  if (rows.length === 0) return <Empty />;
  return (
    <table>
      <thead>
        <tr>
          <th>計上日</th>
          <th>事業部</th>
          <th>取引先</th>
          <th>摘要</th>
          <th>請求書</th>
          <th className="num">売上額</th>
          <th className="num">入金済み</th>
          <th className="num">残高</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.transaction.id}>
            <td>{toShortDate(r.transaction.posting_date)}</td>
            <td>{deptName(r.transaction.department_id)}</td>
            <td>{partyOf(r.transaction)}</td>
            <td>{r.transaction.description ?? '—'}</td>
            <td>{r.invoiceNumber ?? '未請求'}</td>
            <td className="num">{formatYen(r.amount)}</td>
            <td className="num">{formatYen(r.received)}</td>
            <td className={`num${r.balance < 0 ? ' neg' : ''}`} style={{ fontWeight: 700 }}>
              {formatYen(r.balance)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PayableTable({ rows, partyOf, deptName }: { rows: Balances['payables']; partyOf: NameOf; deptName: (id: string) => string }) {
  if (rows.length === 0) return <Empty />;
  return (
    <table>
      <thead>
        <tr>
          <th>計上日</th>
          <th>管理番号</th>
          <th>事業部</th>
          <th>支払先</th>
          <th>摘要</th>
          <th>支払方法</th>
          <th>支払予定日</th>
          <th>状態</th>
          <th className="num">残高</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const t = r.transaction;
          return (
            <tr key={t.id}>
              <td>{toShortDate(t.posting_date)}</td>
              <td>{t.code ?? '—'}</td>
              <td>{deptName(t.department_id)}</td>
              <td>{partyOf(t)}</td>
              <td>{t.description ?? '—'}</td>
              <td>{t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '—'}</td>
              <td>{t.payment_date ? toShortDate(t.payment_date) : '—'}</td>
              <td>{payableState(t)}</td>
              <td className="num" style={{ fontWeight: 700 }}>
                {formatYen(r.balance)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PrepaidTable({
  rows,
  userName,
  deptName,
}: {
  rows: Balances['prepaids'];
  userName: (id: string | null) => string;
  deptName: (id: string) => string;
}) {
  if (rows.length === 0) return <Empty />;
  return (
    <table>
      <thead>
        <tr>
          <th>仮払日</th>
          <th>管理番号</th>
          <th>事業部</th>
          <th>申請者</th>
          <th>摘要</th>
          <th>状態</th>
          <th className="num">残高</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const t = r.transaction;
          return (
            <tr key={t.id}>
              <td>{toShortDate(r.prepaidDate)}</td>
              <td>{t.code ?? '—'}</td>
              <td>{deptName(t.department_id)}</td>
              <td>{userName(t.created_by ?? null)}</td>
              <td>{t.description ?? '—'}</td>
              <td>{STATUS_LABELS[t.status]}</td>
              <td className="num" style={{ fontWeight: 700 }}>
                {formatYen(r.balance)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
